import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Bell, Check, X, Crown } from "lucide-react";

export default function PendingApprovalsDialog({ open, onOpenChange, onChanged, allManagers = [] }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(null); // user_id currently being acted on
  const [decisions, setDecisions] = useState({}); // user_id => {user_type, manager_id}

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/users/pending-approvals");
      setItems(r.data.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error cargando pendientes");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) load();
  }, [open]);

  const setDec = (uid, key, val) => {
    setDecisions((prev) => ({ ...prev, [uid]: { ...(prev[uid] || {}), [key]: val } }));
  };

  const decide = async (uid, action) => {
    setBusy(uid);
    try {
      const dec = decisions[uid] || {};
      const payload = { action };
      if (action === "approve") {
        payload.user_type = dec.user_type || "salesperson";
        if (payload.user_type === "salesperson" && dec.manager_id) {
          payload.manager_id = dec.manager_id;
        }
      }
      await api.post(`/admin/users/${uid}/approval`, payload);
      toast.success(action === "approve" ? "Usuario aprobado" : "Usuario rechazado");
      setItems((prev) => prev.filter((u) => u.user_id !== uid));
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al procesar");
    } finally {
      setBusy(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-w-2xl max-h-[85vh] flex flex-col" data-testid="approvals-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading inline-flex items-center gap-2">
            <Bell className="h-5 w-5 text-[#F4B81F]" /> Aprobaciones pendientes
          </DialogTitle>
          <DialogDescription className="text-xs text-[#6B7280]">
            Usuarios que entraron por Google y aún no tienen rol asignado, o que pidieron ser manager.
            Decide quién entra como vendedor, quién como manager, o rechaza el acceso.
          </DialogDescription>
        </DialogHeader>

        <div className="overflow-y-auto -mx-6 px-6 flex-1">
          {loading ? (
            <p className="text-sm text-[#6B7280] py-8 text-center">Cargando...</p>
          ) : items.length === 0 ? (
            <div className="bg-[#F3F4F6] rounded-xl p-8 text-center">
              <Check className="h-8 w-8 text-[#1B2E7A] mx-auto mb-2" />
              <p className="text-sm text-[#1A1A1A] font-medium">Todo al día</p>
              <p className="text-xs text-[#6B7280] mt-1">No hay usuarios esperando aprobación.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {items.map((u) => {
                const dec = decisions[u.user_id] || { user_type: u.user_type || "salesperson", manager_id: u.manager_id || "" };
                const isMgr = dec.user_type === "manager";
                return (
                  <div
                    key={u.user_id}
                    className="bg-white border-2 border-[#F4B81F]/30 rounded-xl p-4"
                    data-testid={`pending-row-${u.user_id}`}
                  >
                    <div className="flex items-start justify-between gap-4 mb-3">
                      <div className="flex-1 min-w-0">
                        <p className="font-heading text-base text-[#1A1A1A]">{u.name}</p>
                        <p className="text-xs text-[#6B7280] truncate">{u.email}</p>
                        <p className="text-[10px] text-[#6B7280] mt-0.5">
                          Creado: {new Date(u.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      <span className="text-[10px] uppercase tracking-[0.18em] text-[#B45309] bg-[#FEF3C7] border border-[#F4B81F]/40 px-2 py-1 rounded shrink-0">
                        {u.approval_status === "pending" ? "Pendiente" : "Revisión"}
                      </span>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-2 mb-3">
                      <div>
                        <label className="text-[10px] uppercase tracking-[0.16em] text-[#6B7280]">Aprobar como</label>
                        <select
                          value={dec.user_type}
                          onChange={(e) => setDec(u.user_id, "user_type", e.target.value)}
                          className="w-full mt-1 border border-[#E5E7EB] rounded-md px-3 py-2 text-sm bg-white"
                          data-testid={`pending-type-${u.user_id}`}
                        >
                          <option value="salesperson">Vendedor de piso</option>
                          <option value="manager">Manager (líder de tienda)</option>
                        </select>
                      </div>
                      {!isMgr && allManagers.length > 0 && (
                        <div>
                          <label className="text-[10px] uppercase tracking-[0.16em] text-[#6B7280]">Reporta a</label>
                          <select
                            value={dec.manager_id}
                            onChange={(e) => setDec(u.user_id, "manager_id", e.target.value)}
                            className="w-full mt-1 border border-[#E5E7EB] rounded-md px-3 py-2 text-sm bg-white"
                            data-testid={`pending-mgr-${u.user_id}`}
                          >
                            <option value="">— Sin manager —</option>
                            {allManagers.map((m) => (
                              <option key={m.user_id} value={m.user_id}>{m.name}</option>
                            ))}
                          </select>
                        </div>
                      )}
                    </div>

                    <div className="flex items-center gap-2">
                      <Button
                        onClick={() => decide(u.user_id, "approve")}
                        disabled={busy === u.user_id}
                        className="flex-1 bg-[#1B2E7A] hover:bg-[#13205A] text-white"
                        data-testid={`approve-${u.user_id}`}
                      >
                        {isMgr ? <Crown className="h-4 w-4 mr-1" /> : <Check className="h-4 w-4 mr-1" />}
                        Aprobar como {isMgr ? "manager" : "vendedor"}
                      </Button>
                      <Button
                        onClick={() => decide(u.user_id, "reject")}
                        disabled={busy === u.user_id}
                        variant="outline"
                        className="border-[#DC2626] text-[#DC2626] hover:bg-[#FBE8E2]"
                        data-testid={`reject-${u.user_id}`}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
